// CRM Lists — index of saved filters (segments) across contacts and companies.
// Reuses the saved-filters engine that already powers saved views inside the
// Contacts and Companies tables: one shared pool, surfaced here as a roster.
// Chunk 1: the index. Opening a list (inline results) is chunk 2.
import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import CrmTable from '../../components/CrmTable';
import { CONTACT_COLUMNS, COMPANY_COLUMNS } from '../../components/crmColumns';

const ENTITIES = ['contacts', 'companies'];

export default function CrmLists() {
  const { clientId } = useOutletContext();
  const { getToken } = useAuth();

  const [lists, setLists] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const [query, setQuery] = useState('');
  const [openList, setOpenList] = useState(null);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const results = await Promise.all(ENTITIES.map(async (entity) => {
        const r = await fetch(`${API_BASE}/saved-filters/${entity}?client_id=${parseInt(clientId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!r.ok) return [];
        const rows = await r.json();
        return rows.map(row => ({ ...row, entity }));
      }));
      const merged = results.flat().sort((a, b) => a.name.localeCompare(b.name));
      setLists(merged);
    } catch (e) {
      toast.error('Could not load lists');
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken]);

  useEffect(() => { if (clientId) loadLists(); }, [clientId, loadLists]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!lists.length) return;
      const token = await getToken();
      for (const l of lists) {
        const key = `${l.entity}:${l.id}`;
        try {
          const r = await fetch(`${API_BASE}/filter/${l.entity}/count`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ client_id: parseInt(clientId), tree: l.tree })
          });
          if (!r.ok) continue;
          const d = await r.json();
          if (!cancelled) setCounts(prev => ({ ...prev, [key]: d.total }));
        } catch (_) {}
      }
    })();
    return () => { cancelled = true; };
  }, [lists, clientId, getToken]);

  const del = async (l) => {
    if (!window.confirm(`Delete the list "${l.name}"? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/saved-filters/${l.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Delete failed');
      toast.success('List deleted');
      loadLists();
    } catch (e) { toast.error(e.message); }
  };

  const visible = lists.filter(l =>
    (filterEntity === 'all' || l.entity === filterEntity) &&
    (!query || l.name.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="crml">
      <div className="crml-head">
        <div>
          <h1 className="crml-title">Lists</h1>
          <div className="crml-sub">{lists.length} saved {lists.length === 1 ? 'list' : 'lists'} across contacts and companies</div>
        </div>
      </div>

      <div className="crml-toolbar">
        <div className="crml-search">
          <input className="crml-search-in" placeholder="Search lists" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="crml-seg">
          {['all', 'contacts', 'companies'].map(e => (
            <button key={e} className={'crml-seg-btn' + (filterEntity === e ? ' crml-seg-btn--on' : '')} onClick={() => setFilterEntity(e)}>
              {e === 'all' ? 'All' : e.charAt(0).toUpperCase() + e.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="crml-empty">Loading lists{'\u2026'}</p>
      ) : visible.length === 0 ? (
        <p className="crml-empty">{lists.length === 0 ? 'No lists yet. Save a filter as a list from the Contacts or Companies table to see it here.' : 'No lists match.'}</p>
      ) : (
        <div className="crml-list">
          {visible.map(l => {
            const key = `${l.entity}:${l.id}`;
            const count = counts[key];
            return (
              <div key={key} className="crml-card">
                <div className="crml-card-main" onClick={() => setOpenList(l)}>
                  <div className="crml-card-top">
                    <span className="crml-name">{l.name}</span>
                    <span className={'crml-badge crml-badge--' + l.entity}>{l.entity === 'contacts' ? 'Contacts' : 'Companies'}</span>
                    {l.shared ? <span className="crml-badge crml-badge--shared">Shared</span> : <span className="crml-badge crml-badge--private">Private</span>}
                  </div>
                  <div className="crml-card-meta">
                    {count == null ? 'Counting\u2026' : `${count.toLocaleString()} ${count === 1 ? 'match' : 'matches'}`}
                  </div>
                </div>
                <div className="crml-card-actions">
                  <button className="crml-act" onClick={() => setOpenList(l)}>Open</button>
                  <button className="crml-act crml-act--danger" onClick={() => del(l)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openList && (
        <ListResults list={openList} clientId={clientId} getToken={getToken} onBack={() => setOpenList(null)} />
      )}
    </div>
  );
}

// ListResults — inline results for an opened list. Runs /filter/:entity with the
// list's tree, renders CrmTable, and shows a bulk bar that acts on ticked rows,
// or on the whole list when none ticked. Company lists resolve bulk to contacts.
function ListResults({ list, clientId, getToken, onBack }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [campaigns, setCampaigns] = useState([]);
  const [batches, setBatches] = useState([]);
  const [picker, setPicker] = useState(null);
  const [busy, setBusy] = useState(false);

  const columns = list.entity === 'contacts' ? CONTACT_COLUMNS : COMPANY_COLUMNS;
  const navigate = useNavigate();

  const runFilter = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/filter/${list.entity}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), tree: list.tree, sort, limit: pageSize, offset: page * pageSize })
      });
      const d = await r.json();
      if (r.ok) { setRows(d.rows || []); setTotal(d.total || 0); }
      else { setRows([]); setTotal(0); toast.error(d.error || 'Could not run list'); }
    } catch (e) {
      setRows([]); setTotal(0); toast.error('Could not run list');
    } finally {
      setLoading(false);
    }
  }, [list, clientId, getToken, sort, page, pageSize]);

  useEffect(() => { runFilter(); }, [runFilter]);
  useEffect(() => { setPage(0); }, [sort, list]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const cr = await fetch(`${API_BASE}/filter/${list.entity}/targets/campaign?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cr.ok) setCampaigns(await cr.json());
        const [eb, lb] = await Promise.all([
          fetch(`${API_BASE}/clients/${parseInt(clientId)}/email-batches`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.ok ? x.json() : []),
          fetch(`${API_BASE}/clients/${parseInt(clientId)}/linkedin-batches`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.ok ? x.json() : [])
        ]);
        const norm = (arr, channel) => (Array.isArray(arr) ? arr : []).map(b => ({ value: b.id, label: b.name, channel }));
        setBatches([...norm(eb, 'email'), ...norm(lb, 'linkedin')]);
      } catch (_) {}
    })();
  }, [list.entity, clientId, getToken]);

  const toggle = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelectedIds(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  const onSort = (field) => setSort(s => ({ field, dir: s.field === field && s.dir === 'desc' ? 'asc' : 'desc' }));

  const targetPayload = () => {
    const base = { client_id: parseInt(clientId) };
    if (list.entity === 'companies') base.source_entity = 'companies';
    if (selectedIds.size > 0) base.ids = [...selectedIds];
    else base.filter = list.tree;
    return base;
  };

  const scopeLabel = selectedIds.size > 0 ? `${selectedIds.size} selected` : `all ${total.toLocaleString()}`;

  const pushToCampaign = async (campaignId) => {
    setBusy(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/bulk/push-to-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...targetPayload(), campaign_id: campaignId })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Push failed');
      toast.success(`Pushed ${d.pushed} contact${d.pushed === 1 ? '' : 's'} to the campaign`);
      setPicker(null); setSelectedIds(new Set());
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const addToBatch = async (batchId, channel) => {
    setBusy(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/bulk/add-to-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...targetPayload(), batch_id: batchId, channel })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Add failed');
      toast.success(`Added ${d.added} contact${d.added === 1 ? '' : 's'} to the batch`);
      setPicker(null); setSelectedIds(new Set());
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="crml-results">
      <div className="crml-results-head">
        <button className="crml-act" onClick={onBack}>{'\u2190'} All lists</button>
        <div className="crml-results-title">
          <span className="crml-name">{list.name}</span>
          <span className={'crml-badge crml-badge--' + list.entity}>{list.entity === 'contacts' ? 'Contacts' : 'Companies'}</span>
          <span className="crml-results-count">{total.toLocaleString()} {total === 1 ? 'match' : 'matches'}</span>
        </div>
      </div>

      <div className="crml-bulk">
        <span className="crml-bulk-scope">Acting on <b>{scopeLabel}</b></span>
        <div className="crml-bulk-actions">
          <button className="crml-act" disabled={busy || total === 0} onClick={() => setPicker(picker === 'campaign' ? null : 'campaign')}>Push to campaign</button>
          <button className="crml-act" disabled={busy || total === 0} onClick={() => setPicker(picker === 'batch' ? null : 'batch')}>Add to batch</button>
          {selectedIds.size > 0 && <button className="crml-act" onClick={() => setSelectedIds(new Set())}>Clear</button>}
        </div>
        {picker === 'campaign' && (
          <div className="crml-picker">
            {campaigns.length === 0 && <div className="crml-picker-empty">No campaigns yet.</div>}
            {campaigns.map(c => (
              <button key={c.value} className="crml-picker-item" onClick={() => pushToCampaign(c.value)}>{c.label}{c.meta ? ` \u00B7 ${c.meta}` : ''}</button>
            ))}
          </div>
        )}
        {picker === 'batch' && (
          <div className="crml-picker">
            {batches.length === 0 && <div className="crml-picker-empty">No batches yet.</div>}
            {batches.map(b => (
              <button key={b.channel + b.value} className="crml-picker-item" onClick={() => addToBatch(b.value, b.channel)}>
                <span className={'crml-badge crml-badge--' + (b.channel === 'email' ? 'shared' : 'contacts')}>{b.channel}</span> {b.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <CrmTable
        columns={columns}
        rows={rows}
        loading={loading}
        selectedIds={selectedIds}
        onToggle={toggle}
        onToggleAll={toggleAll}
        sort={sort}
        onSort={onSort}
        onContactClick={(row) => navigate(`/clients/${clientId}/crm/${list.entity === 'contacts' ? 'contacts' : 'companies'}/${row.id}`)}
      />

      <div className="crmc-footer">
        <div className="crmc-pagesize">
          <span>Rows per page</span>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="crmc-pager">
          <button disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</button>
          <span className="crmc-pageno">{page + 1} of {Math.max(1, Math.ceil(total / pageSize))}</span>
          <button disabled={page >= Math.ceil(total / pageSize) - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
        <div className="crmc-rangeinfo">
          {total === 0 ? '0' : `${page * pageSize + 1}\u2013${Math.min(total, page * pageSize + rows.length)}`} of {total.toLocaleString()}
        </div>
      </div>
    </div>
  );
}
